/**
 * NPC templates (ADR-136 decision 2.3): a host-wide authored document, `tools/dev-server/npcs/<id>.json`, checked
 * here. An NPC template is a creature template whose name is a line record, with a temperament of the four in
 * `./policy.ts`, a level of its own and an optional dialogue and sound. The fight fields (`health`, `abilities`,
 * `noticeTiles`, `leashTiles`, `xp`) are required unless the temperament is `passive`, and are checked by the
 * creature template's own rules. Pure.
 */

import { SLUG_PATTERN } from "../content/slug.ts";
import { validateCreatureTemplate } from "../creatures/template.ts";
import { codePointCount } from "../chat/text.ts";
import { CONTENT_FILE_ID_PATTERN, lineProblems, type LineRecord } from "../content/line.ts";
import { spawnLevelCap } from "../creatures/spawn.ts";
import { MAX_STANDING_TIERS, MAX_TALK_STANDING, NPC_STANDING_MAX, NPC_STANDING_MIN } from "./standing.ts";
import { NPC_TEMPERAMENTS, isNpcTemperament, type NpcTemperament } from "./policy.ts";

/**
 * The most code points an NPC's shown name takes, a name part's limit on the wire.
 */
export const MAX_NPC_NAME_CHARS = 32;

const SLUG = SLUG_PATTERN;
const FIGHT_FIELDS = ["health", "abilities", "noticeTiles", "leashTiles", "xp"] as const;
const FIELDS = ["id", "name", "look", "scale", "pace", "restTicks", "temperament", "level", "sound", "dialogue", "standingTiers", "talkStanding", ...FIGHT_FIELDS] as const;
const PASSIVE_FIGHT_DEFAULTS: Readonly<Record<(typeof FIGHT_FIELDS)[number], unknown>> = { health: 1, abilities: [], noticeTiles: 1, leashTiles: 1, xp: 0 };
const MAX_LINE_ID_BOUND = 1_000_000;

/**
 * The sizes an NPC's look may be drawn at, as a factor of its sheet's pixels: exact halves and doubles keep the art's
 * pixels whole. 1 when a template names none.
 */
export const NPC_SCALES = Object.freeze([0.5, 1, 2] as const);

/**
 * One of the sizes an NPC's look may be drawn at.
 */
export type NpcScale = (typeof NPC_SCALES)[number];

/**
 * An NPC template. The fight fields are absent from a passive template that names none.
 */
export interface NpcTemplate {
  readonly id: string;
  /** The shown name, a line record with no speaker. */
  readonly name: LineRecord;
  readonly look: string;
  /** The size the look is drawn at (`NPC_SCALES`); absent for 1. */
  readonly scale?: NpcScale;
  readonly pace: number;
  readonly restTicks: readonly [number, number];
  readonly temperament: NpcTemperament;
  readonly level: number;
  readonly health?: number;
  readonly abilities?: readonly string[];
  readonly noticeTiles?: number;
  readonly leashTiles?: number;
  readonly xp?: number;
  /** The sound folder under `sfx/npcs/`; absent for an NPC that makes none. */
  readonly sound?: string;
  /** The default dialogue file's id; absent for an NPC with none. */
  readonly dialogue?: string;
  /** The names of the standing tiers (ADR-146 decision 3.3), ascending, the first from -100; absent for the default table. */
  readonly standingTiers?: readonly NpcStandingTier[];
  /** The standing a character gains the first time in an in-game day it opens a conversation with the NPC (ADR-146 amendment of 2026-10-31), 1 to `MAX_TALK_STANDING`; absent for none. */
  readonly talkStanding?: number;
}

/**
 * One tier of an NPC's standing: its shown name, a line record with no speaker, and the standing it starts at.
 */
export interface NpcStandingTier {
  readonly name: LineRecord;
  readonly from: number;
}

/**
 * The verdict on one template document.
 */
export type NpcTemplateVerdict = { readonly ok: true; readonly template: NpcTemplate } | { readonly ok: false; readonly errors: readonly string[] };

/**
 * Checks an NPC template as its file holds it. `temperament` may be left out and is then `passive`, `level` may be left
 * out and is then 1, `scale` may be left out and is then 1 (else one of `NPC_SCALES`), and a passive template may leave out every fight field; any it gives are checked. The fight fields,
 * `look`, `pace` and `restTicks` are checked by `validateCreatureTemplate`, so an NPC's numbers follow a creature's.
 *
 * @param value The parsed file.
 * @param fileId The file's name without `.json`, which the `id` must equal.
 * @param topPercent The health percent of the creature table's last level (`topHealthPercent`), which bounds a template's health.
 * @returns The template, or every error found.
 * @example
 * validateNpcTemplate(JSON.parse(text), "guard", topHealthPercent(rules.tables.creature_levels));
 */
export function validateNpcTemplate(value: unknown, fileId: string, topPercent: number): NpcTemplateVerdict {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return { ok: false, errors: [`${fileId}: a template must be a JSON object`] };
  }
  const record = value as Readonly<Record<string, unknown>>;
  const errors: string[] = [];
  /**
   * Records one error against the template.
   * @param message What is wrong with which field.
   */
  const fail = (message: string): void => {
    errors.push(`${fileId}: ${message}`);
  };
  const id = record["id"];
  if (typeof id !== "string" || !SLUG.test(id)) {
    fail("id must be lower-case letters, digits and -");
  } else if (id !== fileId) {
    fail(`id must be ${fileId}, the file's name, not ${id}`);
  }
  nameErrors(record["name"]).forEach(fail);
  const temperament = record["temperament"] ?? "passive";
  if (!isNpcTemperament(temperament)) {
    fail(`temperament must be ${NPC_TEMPERAMENTS.slice(0, -1).join(", ")} or ${NPC_TEMPERAMENTS[NPC_TEMPERAMENTS.length - 1] as string}`);
  }
  const passive = temperament === "passive";
  const forCreature: Record<string, unknown> = { id: fileId, name: "", look: record["look"], level: 1, pace: record["pace"], restTicks: record["restTicks"], temperament: "neutral", threatTiles: 1 };
  for (const field of FIGHT_FIELDS) {
    forCreature[field] = record[field] === undefined && passive ? PASSIVE_FIGHT_DEFAULTS[field] : record[field];
  }
  const creature = validateCreatureTemplate(forCreature, fileId, topPercent);
  if (!creature.ok) {
    errors.push(...creature.errors);
  }
  const cap = spawnLevelCap();
  const level = record["level"] ?? 1;
  if (typeof level !== "number" || !Number.isInteger(level) || level < 1 || level > cap) {
    fail(`level must be an integer from 1 to ${cap}`);
  }
  const dialogue = record["dialogue"];
  if (dialogue !== undefined && (typeof dialogue !== "string" || !CONTENT_FILE_ID_PATTERN.test(dialogue))) {
    fail("dialogue must name a dialogue file in lower-case letters, digits, _ and -");
  }
  const tiers = record["standingTiers"];
  if (tiers !== undefined) {
    standingTierErrors(tiers, record["name"]).forEach(fail);
  }
  const talk = record["talkStanding"];
  if (talk !== undefined && (typeof talk !== "number" || !Number.isInteger(talk) || talk < 1 || talk > MAX_TALK_STANDING)) {
    fail(`talkStanding must be a whole number from 1 to ${MAX_TALK_STANDING}`);
  }
  const scale = record["scale"];
  if (scale !== undefined && !(NPC_SCALES as readonly unknown[]).includes(scale)) {
    fail(`scale must be ${NPC_SCALES.join(", ")}`);
  }
  const sound = record["sound"];
  if (sound !== undefined && (typeof sound !== "string" || !SLUG.test(sound))) {
    fail("sound must name a sound folder in lower-case letters, digits and -");
  }
  for (const key of Object.keys(record)) {
    if (!(FIELDS as readonly string[]).includes(key)) {
      fail(`${key} is not a template field`);
    }
  }
  if (errors.length > 0 || !creature.ok) {
    return { ok: false, errors };
  }
  /**
   * Whether the template names a fight field.
   * @param field The field.
   * @returns True when present.
   */
  const given = (field: (typeof FIGHT_FIELDS)[number]): boolean => record[field] !== undefined;
  const made = creature.template;
  return {
    ok: true,
    template: {
      id: id as string,
      name: record["name"] as LineRecord,
      look: made.look,
      ...(scale === undefined || scale === 1 ? {} : { scale: scale as NpcScale }),
      pace: made.pace,
      restTicks: made.restTicks,
      temperament: temperament as NpcTemperament,
      level: level as number,
      ...(given("health") ? { health: made.health } : {}),
      ...(given("abilities") ? { abilities: made.abilities } : {}),
      ...(given("noticeTiles") ? { noticeTiles: made.noticeTiles } : {}),
      ...(given("leashTiles") ? { leashTiles: made.leashTiles } : {}),
      ...(given("xp") ? { xp: made.xp } : {}),
      ...(sound === undefined ? {} : { sound: sound as string }),
      ...(dialogue === undefined ? {} : { dialogue: dialogue as string }),
      ...(tiers === undefined ? {} : { standingTiers: tiers as readonly NpcStandingTier[] }),
      ...(talk === undefined ? {} : { talkStanding: talk as number }),
    },
  };
}

/**
 * The problems of a template's name: a line record with a null speaker and a plain text of 1 to `MAX_NPC_NAME_CHARS` code points.
 *
 * @param name The `name` field.
 * @param label What the problems call the record: `name`, or the path of a tier's name.
 * @returns The sentences after the template's name; empty when the name is sound.
 */
function nameErrors(name: unknown, label = "name"): string[] {
  if (typeof name !== "object" || name === null || Array.isArray(name) || Object.keys(name).sort().join() !== "id,note,speaker,text") {
    return [`${label} must be a line record: id, speaker, text and note`];
  }
  const record = name as Readonly<Record<string, unknown>>;
  const errors: string[] = [];
  if (record["speaker"] !== null) {
    errors.push(`${label} must have a null speaker`);
  }
  if (typeof record["text"] !== "string" && record["text"] !== undefined && typeof record["text"] === "object") {
    errors.push(`${label} text must be plain text`);
  } else if (typeof record["text"] === "string" && codePointCount(record["text"]) > MAX_NPC_NAME_CHARS) {
    errors.push(`${label} text must be at most ${MAX_NPC_NAME_CHARS} characters`);
  }
  if (errors.length === 0 && lineProblems([name], MAX_LINE_ID_BOUND).length > 0) {
    errors.push(`${label} must be a valid line record: an id such as l1, a non-empty text and a note`);
  }
  return errors;
}

/**
 * The problems of a template's `standingTiers`: a list of 1 to 8 closed entries of a name (a plain line record with a null
 * speaker) and a whole `from`, strictly ascending from -100, with line ids that differ from each other and from the template name's.
 *
 * @param tiers The `standingTiers` field.
 * @param name The template's `name` field, whose line id the tiers may not reuse.
 * @returns The sentences after the template's name; empty when the tiers are sound.
 */
function standingTierErrors(tiers: unknown, name: unknown): string[] {
  if (!Array.isArray(tiers) || tiers.length < 1 || tiers.length > MAX_STANDING_TIERS) {
    return [`standingTiers must be a list of 1 to ${MAX_STANDING_TIERS} tiers, each with name and from`];
  }
  const errors: string[] = [];
  const ids: unknown[] = [];
  /**
   * Remembers the line id of a line record, if it has one.
   * @param record The line record as read.
   */
  const noted = (record: unknown): void => {
    const id = typeof record === "object" && record !== null ? (record as Readonly<Record<string, unknown>>)["id"] : undefined;
    if (typeof id === "string") {
      ids.push(id);
    }
  };
  noted(name);
  let before = Number.NEGATIVE_INFINITY;
  tiers.forEach((tier, at) => {
    const here = `standingTiers[${at}]`;
    if (typeof tier !== "object" || tier === null || Array.isArray(tier) || Object.keys(tier).sort().join() !== "from,name") {
      errors.push(`${here} must hold exactly name and from`);
      return;
    }
    const entry = tier as Readonly<Record<string, unknown>>;
    errors.push(...nameErrors(entry["name"], `${here}.name`));
    noted(entry["name"]);
    const from = entry["from"];
    if (typeof from !== "number" || !Number.isInteger(from) || from < NPC_STANDING_MIN || from > NPC_STANDING_MAX) {
      errors.push(`${here}.from must be a whole number from ${NPC_STANDING_MIN} to ${NPC_STANDING_MAX}`);
      return;
    }
    if (at === 0 && from !== NPC_STANDING_MIN) {
      errors.push(`${here}.from must be ${NPC_STANDING_MIN}, the bottom of the range`);
    } else if (at > 0 && from <= before) {
      errors.push(`${here}.from must be above the tier before it`);
    }
    before = from;
  });
  if (new Set(ids).size !== ids.length) {
    errors.push("standingTiers line ids must differ from each other and from the name's");
  }
  return errors;
}
